import {
  CandidateExternalProcessStatusDto,
  CandidateMatchComponentTypeDto,
  CandidateRateSourceDto,
  CandidateSourceDto,
  CandidatesSortByDto,
  ExperienceCompanyTypeDto,
} from '@/api/generated';
import {
  candidateStatusesTranslation,
  CandidateStatusesTranslation,
} from '@/assets/locales/views/candidates/details/candidateDetails.translation.en.ts';
import { ProcessCandidatesSearchFormModel } from '@/views/processes/details/model/ProcessCandidatesSearchForm.model.ts';

export type CandidateExternalProcessStatusTranslation = Record<
  CandidateExternalProcessStatusDto,
  string
>;
export const candidateExternalProcessStatusTranslation: CandidateExternalProcessStatusTranslation =
  {
    [CandidateExternalProcessStatusDto.MarkedAsSent]: 'Manually',
    [CandidateExternalProcessStatusDto.NotApplicable]: 'N/A',
    [CandidateExternalProcessStatusDto.NotSent]: 'No',
    [CandidateExternalProcessStatusDto.Sent]: 'Yes',
  };

export type ProcessCandidatesListTranslation = {
  applicationSource: string;
  body: {
    actions: string;
    applicationSources: string;
    candidateName: string;
    city: string;
    componentMatch: Record<CandidateMatchComponentTypeDto, string>;
    country: string;
    expectedSalary: string;
    isInternalCandidate: string;
    languages: string;
    ordinalNumber: string;
    rateSource: string;
    tags: string;
    voivodeship: string;
  } & Record<
    Exclude<
      CandidatesSortByDto,
      typeof CandidatesSortByDto.FirstName | typeof CandidatesSortByDto.LastName
    >,
    string
  >;
  dialog: {
    candidatesNotSentToExternalRecruitment: string;
    confirmationAllSentToContent: string;
    confirmationMarkAsNotSentContent: string;
    confirmationMarkAsSentContent: string;
    confirmationReprocessContent: string;
    confirmationReprocessSelectedCandidates: string;
    confirmationSentToERecruiter: string;
    confirmationSentToERecruiterWarning: string;
    infoContent: string;
  };
  experienceCompanyTypes: Record<ExperienceCompanyTypeDto, string>;
  externalProcessStatuses: CandidateExternalProcessStatusTranslation;
  head: Record<keyof ProcessCandidatesSearchFormModel, string>;
  id: string;
  labels: {
    discovery: string;
    eRecruiter: string;
    refreshAnalysis: string;
    sendTo: string;
    uploadCV: string;
  };
  rateSources: Record<CandidateRateSourceDto, string>;
  sources: Record<CandidateSourceDto, string>;
  statuses: CandidateStatusesTranslation;
  statusInfo: {
    error: string;
  };
  title: string;
  tooltip: {
    discover: string;
    markAsNotSent: string;
    markAsSent: string;
    matchComponents: string;
    refresh: string;
  };
  tour: {
    experienceCompanyTypesFilter: {
      text: string;
      title: string;
    };
    markAsSent: {
      text: string;
      title: string;
    };
    matchColumn: {
      text: string;
      title: string;
    };
    tagsColumn: {
      text: string;
      title: string;
    };
  };
};

export const processCandidatesListTranslation: ProcessCandidatesListTranslation = {
  applicationSource: 'Application source',
  body: {
    actions: 'Actions',
    applicationSources: 'Application source',
    candidateName: 'Name',
    [CandidatesSortByDto.Date]: 'Date',
    [CandidatesSortByDto.ExternalProcessStatus]: 'Sent',
    [CandidatesSortByDto.Match]: 'Match',
    [CandidatesSortByDto.OrdinalNumber]: 'ID',
    [CandidatesSortByDto.Rate]: 'Rate',
    [CandidatesSortByDto.Source]: 'Source',
    [CandidatesSortByDto.Status]: 'Status',
    city: 'City',
    componentMatch: {
      [CandidateMatchComponentTypeDto.CompanyLoyalty]: 'Loyalty',
      [CandidateMatchComponentTypeDto.IndustryExperience]: 'Preferable industry experience',
      [CandidateMatchComponentTypeDto.MainAnalysis]: 'Main analysis',
    },
    country: 'Country',
    expectedSalary: 'Expected salary',
    isInternalCandidate: 'Internal candidate',
    languages: 'Languages',
    ordinalNumber: 'ID',
    rateSource: 'Rate source',
    tags: 'Tags',
    voivodeship: 'Voivodeship',
  },
  dialog: {
    candidatesNotSentToExternalRecruitment:
      'Candidates with the following identifiers: {{ids}} cannot be moved due to their status or source. Please analyze each candidate individually.',
    confirmationAllSentToContent:
      "Are you sure you want to move all candidates to 'Selected by AI' status? Please confirm to proceed.",
    confirmationMarkAsNotSentContent:
      'Are you sure you want to mark this candidate as NOT SENT? Make sure candidate is in appropriate status in eRecruiter',
    confirmationMarkAsSentContent:
      'Are you sure you want to mark this candidate as SENT? Make sure candidate is in appropriate status in eRecruiter',
    confirmationReprocessContent:
      'Are you sure you want to refresh this candidate? Please confirm to proceed.',
    confirmationReprocessSelectedCandidates:
      'Are you sure you want to refresh selected candidates?',
    confirmationSentToERecruiter:
      "Are you sure you want to move the candidate to 'Selected by AI' status? Please confirm to proceed.",
    confirmationSentToERecruiterWarning:
      "Please note that candidates in state 'ERROR' will NOT be processed",
    infoContent:
      'Candidates with the following identifiers: {{ids}} cannot be re-submitted for evaluation due to their status. Please analyze each candidate individually.',
  },
  experienceCompanyTypes: {
    [ExperienceCompanyTypeDto.Competitor]: 'Competitor',
    [ExperienceCompanyTypeDto.None]: 'None',
    [ExperienceCompanyTypeDto.Orange]: 'Orange',
    [ExperienceCompanyTypeDto.Partner]: 'Partner',
  },
  externalProcessStatuses: candidateExternalProcessStatusTranslation,
  head: {
    applicationSources: 'Application source',
    expectedSalary: 'Salary up to',
    experienceCompanyTypes: 'Experience company types',
    externalProcessStatus: 'Sent',
    isInternalCandidate: 'Internal candidate',
    location: 'Location',
    ordinalNumber: 'ID',
    rate: 'Rate',
    searchPhrase: 'Search by first name, last name',
    statuses: 'Status',
    voivodeships: 'Voivodeship',
  },
  id: 'Id',
  labels: {
    discovery: 'Discovery',
    eRecruiter: 'eRecruiter',
    refreshAnalysis: 'Refresh Analysis',
    sendTo: 'Send to',
    uploadCV: 'Upload CV (PDF or ZIP)',
  },
  rateSources: {
    [CandidateRateSourceDto.Ai]: 'AI',
    [CandidateRateSourceDto.Recruiter]: 'Recruiter',
  },
  sources: {
    [CandidateSourceDto.Erecruiter]: 'eRecruiter',
    [CandidateSourceDto.KnowMe]: 'KnowMe',
  },
  statuses: candidateStatusesTranslation,
  statusInfo: {
    error: 'Error',
  },
  title: 'Candidates',
  tooltip: {
    discover: 'Details',
    markAsNotSent: 'Mark as not sent',
    markAsSent: 'Mark as sent',
    matchComponents: 'Match components',
    refresh: 'Refresh Analysis',
  },
  tour: {
    experienceCompanyTypesFilter: {
      text: 'Candidates are assigned to one of these experience groups: Orange, Partner, Competitor, or None. Use this filter to show candidates with the experience group you need.',
      title: 'Experience group filter',
    },
    markAsSent: {
      text: "You can change candidate's sent state without calling eRecruiter",
      title: 'Mark as sent',
    },
    matchColumn: {
      text: 'For B2C processes, candidates can receive extra points, including for stability, loyalty, rarely changing employers, and work in preferred industries. When the info icon appears in Match, hover it to see the score components.',
      title: 'Match score details',
    },
    tagsColumn: {
      text: 'Experience tags appear here. They show whether the candidate has experience in Orange, Partner, Competitor, or None groups.',
      title: 'Experience tags',
    },
  },
};
